<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <!-- Header -->
        <header class="inventario-header">
          <div>
            <p class="almacen-title">Inventario global</p>
            <p class="almacen-subtitle">
              Resumen actual de unidades por producto en almacén y asignadas, con detalle de cada unidad.
            </p>
          </div>

          <div class="inventario-header__right" v-if="!cargando && productosFiltrados.length">
            <div class="inventario-summary">
              <span class="chip chip-soft">
                Productos: {{ resumenGlobal.totalProductos }}
              </span>
              <span class="chip chip-soft chip-soft--total">
                Unidades: {{ resumenGlobal.totalUnidades }}
              </span>
              <span class="chip chip-soft chip-soft--almacen">
                En almacén: {{ resumenGlobal.totalAlmacen }}
              </span>
              <span class="chip chip-soft chip-soft--asignado">
                Asignadas: {{ resumenGlobal.totalAsignadas }}
              </span>
            </div>
          </div>
        </header>

        <!-- Buscador -->
        <section class="inventario-search-row">
          <div class="inventario-search">
            <div class="inventario-search-top">
              <div class="inventario-mode-toggle">
                <button
                  type="button"
                  class="mode-pill"
                  :class="{ 'mode-pill--active': modoBusqueda === 'producto' }"
                  @click="cambiarModoBusqueda('producto')"
                >
                  Por producto
                </button>
                <button
                  type="button"
                  class="mode-pill"
                  :class="{ 'mode-pill--active': modoBusqueda === 'serie' }"
                  @click="cambiarModoBusqueda('serie')"
                >
                  Por número de serie
                </button>
              </div>
            </div>

            <div class="inventario-search-row-inner">
              <input
                v-model="busqueda"
                type="text"
                :placeholder="placeholderBusqueda"
                @keyup.enter="onEnterBusqueda"
              >

              <button
                v-if="modoBusqueda === 'serie'"
                type="button"
                class="inventario-search-btn"
                :disabled="buscandoSerie"
                @click="buscarPorNumeroSerie"
              >
                {{ buscandoSerie ? 'Buscando…' : 'Buscar' }}
              </button>
            </div>
          </div>
        </section>

        <!-- Panel principal -->
        <section class="inventario-panel">
          <div v-if="cargando" class="estado-centrado">
            <div class="spinner"></div>
            <p>Cargando inventario global…</p>
          </div>

          <div v-else-if="!productosFiltrados.length" class="estado-centrado estado-vacio">
            <p>No se encontraron productos para mostrar.</p>
          </div>

          <div v-else class="inventario-list">
            <article
              v-for="p in productosFiltrados"
              :key="p.id_producto"
              class="inventario-item"
              :data-producto-id="p.id_producto"
            >
              <!-- Fila principal de producto -->
              <button
                type="button"
                class="inventario-item-main"
                @click="toggleProducto(p)"
              >
                <div class="inventario-info">
                  <p class="inventario-nombre">
                    {{ p.nombre }}
                  </p>
                  <p class="inventario-token">
                    {{ p.token_producto }}
                  </p>
                </div>

                <div class="inventario-counts">
                  <span class="chip chip-soft chip-soft--total">
                    Total: {{ p.total_unidades || 0 }}
                  </span>
                  <span
                    v-if="toNumber(p.unidades_almacen)"
                    class="chip chip-soft chip-soft--almacen"
                  >
                    Almacén: {{ p.unidades_almacen }}
                  </span>
                  <span
                    v-if="toNumber(p.unidades_asignadas)"
                    class="chip chip-soft chip-soft--asignado"
                  >
                    Asignadas: {{ p.unidades_asignadas }}
                  </span>
                </div>

                <div class="inventario-expand-indicator">
                  <span v-if="productoExpandido && productoExpandido.id_producto === p.id_producto">
                    ▲
                  </span>
                  <span v-else>
                    ▼
                  </span>
                </div>
              </button>

              <!-- Segunda línea: otros estados -->
              <div class="inventario-extra" v-if="tieneOtrosEstados(p)">
                <span
                  v-if="toNumber(p.unidades_resguardo)"
                  class="chip chip-soft chip-soft--resguardo"
                >
                  Resguardo: {{ p.unidades_resguardo }}
                </span>
                <span
                  v-if="toNumber(p.unidades_merma)"
                  class="chip chip-soft chip-soft--merma"
                >
                  Merma: {{ p.unidades_merma }}
                </span>
                <span
                  v-if="toNumber(p.unidades_devuelto)"
                  class="chip chip-soft chip-soft--devuelto"
                >
                  Devueltas: {{ p.unidades_devuelto }}
                </span>
              </div>

              <!-- Bloque de unidades (solo producto expandido) -->
              <transition name="fade-slide">
                <div
                  v-if="productoExpandido && productoExpandido.id_producto === p.id_producto"
                  class="inventario-unidades-block"
                >
                  <div class="unidades-header">
                    <p class="unidades-title">
                      Unidades de <strong>{{ p.nombre }}</strong>
                    </p>
                    <p class="unidades-subtitle">
                      Estado actual de cada unidad. Haz clic en el producto para contraer.
                    </p>
                  </div>

                  <div v-if="cargandoUnidades" class="estado-centrado unidades-loading">
                    <div class="spinner"></div>
                    <p>Cargando unidades del producto…</p>
                  </div>

                  <div
                    v-else-if="!unidadesOrdenadas.length"
                    class="estado-centrado estado-vacio unidades-empty"
                  >
                    <p>Este producto aún no tiene unidades registradas.</p>
                  </div>

                  <div v-else class="unidades-list">
                    <div
                      v-for="u in unidadesOrdenadas"
                      :key="u.id_unidad"
                      class="unidad-row"
                      :class="{ 'unidad-row--highlight': u.id_unidad === unidadResaltadaId }"
                      :data-unidad-id="u.id_unidad"
                    >
                      <div class="unidad-main">
                        <div class="unidad-serie-token">
                          <p class="unidad-serie">
                            {{ u.numero_serie || 'Sin número de serie' }}
                          </p>
                          <p class="unidad-token">
                            {{ u.token_unidad }}
                          </p>
                        </div>

                        <div class="unidad-meta">
                          <span
                            class="chip chip-outline"
                            :class="'chip-estado--' + (u.estado || 'almacen')"
                          >
                            {{ etiquetaEstadoUnidad(u.estado) }}
                          </span>

                          <span
                            v-if="u.folio_entrada"
                            class="chip chip-soft chip-soft--entrada"
                          >
                            Entrada: {{ u.folio_entrada }} ·
                            {{ formatFechaCorta(u.fecha_entrada) }}
                          </span>

                          <span
                            v-if="u.ubicacion_actual"
                            class="chip chip-soft chip-soft--ubicacion"
                          >
                            {{ u.ubicacion_actual }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </article>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'InventarioGlobal',

  data() {
    return {
      productos: [],
      cargando: false,
      busqueda: '',

      // Segundo nivel (unidades)
      productoExpandido: null,
      unidades: [],
      cargandoUnidades: false,

      // Búsqueda avanzada
      modoBusqueda: 'producto', // 'producto' | 'serie'
      buscandoSerie: false,
      unidadResaltadaId: null,
    };
  },

  computed: {
    placeholderBusqueda() {
      return this.modoBusqueda === 'producto'
        ? 'Buscar producto por nombre o token…'
        : 'Buscar unidad por número de serie o token…';
    },

    productosFiltrados() {
      // En modo serie no filtramos por texto; usamos la búsqueda dedicada.
      if (this.modoBusqueda === 'serie') {
        return this.productos;
      }

      const term = this.busqueda.trim().toLowerCase();
      if (!term) return this.productos;

      return this.productos.filter((p) => {
        const nombre = (p.nombre || '').toLowerCase();
        const token = (p.token_producto || '').toLowerCase();
        return nombre.includes(term) || token.includes(term);
      });
    },

    resumenGlobal() {
      const resumen = {
        totalProductos: 0,
        totalUnidades: 0,
        totalAlmacen: 0,
        totalAsignadas: 0,
        totalResguardo: 0,
        totalMerma: 0,
        totalDevuelto: 0,
      };

      const lista = this.productosFiltrados;
      resumen.totalProductos = lista.length;

      lista.forEach((p) => {
        const n = (v) => this.toNumber(v);
        resumen.totalUnidades += n(p.total_unidades);
        resumen.totalAlmacen += n(p.unidades_almacen);
        resumen.totalAsignadas += n(p.unidades_asignadas);
        resumen.totalResguardo += n(p.unidades_resguardo);
        resumen.totalMerma += n(p.unidades_merma);
        resumen.totalDevuelto += n(p.unidades_devuelto);
      });

      return resumen;
    },

    unidadesOrdenadas() {
      return [...this.unidades].sort((a, b) => {
        const sa = (a.numero_serie || '').toString();
        const sb = (b.numero_serie || '').toString();
        return sa.localeCompare(sb, 'es-MX', { numeric: true });
      });
    },
  },

  mounted() {
    this.cargarProductos();
  },

  methods: {
    /* --------- utilidades básicas --------- */

    toNumber(value) {
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    },

    tieneOtrosEstados(p) {
      return (
        this.toNumber(p.unidades_resguardo) ||
        this.toNumber(p.unidades_merma) ||
        this.toNumber(p.unidades_devuelto)
      );
    },

    /* --------- modo de búsqueda --------- */

    cambiarModoBusqueda(modo) {
      if (modo === this.modoBusqueda) return;
      this.modoBusqueda = modo;
      this.busqueda = '';
      this.unidadResaltadaId = null;
    },

    onEnterBusqueda() {
      if (this.modoBusqueda === 'serie') {
        this.buscarPorNumeroSerie();
      }
    },

    async buscarPorNumeroSerie() {
      const termino = this.busqueda.trim();
      if (!termino) return;

      this.buscandoSerie = true;
      this.unidadResaltadaId = null;

      try {
        const res = await axios.get('/api/almacen/historial-unidades/buscar-unidad', {
          params: { numero_serie: termino },
        });

        const payload = res.data || {};
        const unidad = payload?.data?.unidad || null;
        const producto = payload?.data?.producto || null;

        if (!unidad || !producto) {
          this.$toast?.info(
            payload?.message || 'No se encontró ninguna unidad con ese número de serie.'
          );
          return;
        }

        // Localizamos el producto ya cargado para conservar métricas
        const productoLocal = this.productos.find(
          (p) => String(p.id_producto) === String(producto.id_producto)
        );

        this.productoExpandido = productoLocal || producto;

        // Cargamos unidades del producto
        await this.cargarUnidades(this.productoExpandido.id_producto);

        // Buscamos la unidad concreta
        const unidadLocal = this.unidades.find(
          (u) => String(u.id_unidad) === String(unidad.id_unidad)
        );

        if (unidadLocal) {
          this.unidadResaltadaId = unidadLocal.id_unidad;
        } else {
          this.unidades.push(unidad);
          this.unidadResaltadaId = unidad.id_unidad;
        }

        // Scroll automático al producto + unidad dentro de la lista
        this.scrollToProductoYUnidad(this.productoExpandido.id_producto, this.unidadResaltadaId);
      } catch (error) {
        console.error('Error al buscar por número de serie en inventario global:', error);
        this.$toast?.error('No se pudo buscar la unidad por número de serie.');
      } finally {
        this.buscandoSerie = false;
      }
    },

    /* --------- carga de datos --------- */

    async cargarProductos() {
      this.cargando = true;
      try {
        const res = await axios.get('/api/almacen/historial-unidades/productos');
        const payload = res.data;

        this.productos = Array.isArray(payload?.data?.productos)
          ? payload.data.productos
          : [];
      } catch (error) {
        console.error('Error al cargar inventario global:', error);
        this.$toast?.error('No se pudo cargar el inventario global.');
        this.productos = [];
      } finally {
        this.cargando = false;
      }
    },

    async cargarUnidades(idProducto) {
      if (!idProducto) return;
      this.cargandoUnidades = true;
      this.unidades = [];
      this.unidadResaltadaId = null;

      try {
        const res = await axios.get('/api/almacen/historial-unidades/unidades', {
          params: { id_producto: idProducto },
        });
        const payload = res.data;

        this.unidades = Array.isArray(payload?.data?.unidades)
          ? payload.data.unidades
          : [];
      } catch (error) {
        console.error('Error al cargar unidades del producto:', error);
        this.$toast?.error('No se pudieron cargar las unidades del producto.');
        this.unidades = [];
      } finally {
        this.cargandoUnidades = false;
      }
    },

    async toggleProducto(producto) {
      if (
        this.productoExpandido &&
        this.productoExpandido.id_producto === producto.id_producto
      ) {
        // Colapsar si es el mismo
        this.productoExpandido = null;
        this.unidades = [];
        this.unidadResaltadaId = null;
        return;
      }

      this.productoExpandido = producto;
      await this.cargarUnidades(producto.id_producto);

      // Al abrir manualmente un producto, al menos lo centramos en la vista
      this.scrollToProductoYUnidad(producto.id_producto, null);
    },

    /* --------- scroll automático --------- */

    scrollToProductoYUnidad(idProducto, idUnidad = null) {
      this.$nextTick(() => {
        const root = this.$el;
        if (!root) return;

        // 1) Llevar el card de producto al centro de la vista
        const articulo = root.querySelector(
          `.inventario-item[data-producto-id="${idProducto}"]`
        );
        if (!articulo) return;

        articulo.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 2) Si se especifica unidad, hacer scroll dentro de la lista de unidades
        if (!idUnidad) return;

        const listaUnidades = articulo.querySelector('.unidades-list');
        if (!listaUnidades) return;

        const fila = listaUnidades.querySelector(
          `.unidad-row[data-unidad-id="${idUnidad}"]`
        );
        if (!fila) return;

        const offset =
          fila.offsetTop - listaUnidades.offsetTop - 24; // pequeño margen superior

        listaUnidades.scrollTo({
          top: offset < 0 ? 0 : offset,
          behavior: 'smooth',
        });
      });
    },

    /* --------- etiquetas / formatos --------- */

    etiquetaEstadoUnidad(estado) {
      switch (estado) {
        case 'almacen':
          return 'En almacén';
        case 'asignado':
          return 'Asignado';
        case 'resguardo':
          return 'En resguardo';
        case 'merma':
          return 'Merma';
        case 'devuelto':
          return 'Devuelto';
        default:
          return estado || 'Desconocido';
      }
    },

    formatFechaCorta(fechaStr) {
      if (!fechaStr) return 'Sin fecha';
      try {
        const d = new Date(fechaStr);
        if (Number.isNaN(d.getTime())) return fechaStr;
        return d.toLocaleDateString('es-MX', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch {
        return fechaStr;
      }
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Header */
.inventario-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.inventario-header__right {
  display: flex;
  align-items: center;
}

/* Summary chips */
.inventario-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  justify-content: flex-end;
}

/* Search */
.inventario-search-row {
  margin-bottom: 0.6rem;
}

.inventario-search-top {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.inventario-mode-toggle {
  display: inline-flex;
  padding: 0.12rem;
  border-radius: 999px;
  background: radial-gradient(
    circle at 0 0,
    rgba(30, 64, 175, 0.9),
    rgba(15, 23, 42, 0.96)
  );
  border: 1px solid rgba(129, 140, 248, 0.9);
}

.mode-pill {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.7rem;
  padding: 0.18rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease-out;
}

.mode-pill--active {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 1),
    rgba(30, 64, 175, 1)
  );
  color: #050816;
  box-shadow: 0 0 14px rgba(129, 140, 248, 0.75);
}

.inventario-search-row-inner {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.inventario-search input {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.45rem 0.9rem;
  font-size: 0.8rem;
  color: #e5e7eb;
  outline: none;
}

.inventario-search input::placeholder {
  color: #64748b;
}

.inventario-search-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.98);
  color: #e5e7eb;
  font-size: 0.75rem;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.inventario-search-btn:hover:enabled {
  background: rgba(15, 23, 42, 1);
  transform: translateY(-0.5px);
}

.inventario-search-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Panel */
.inventario-panel {
  border-radius: 1.2rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.94);
  padding: 0.75rem 0.65rem;
}

/* Lista */
.inventario-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: calc(100vh - 260px);
  overflow: auto;
}

.inventario-item {
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.98);
  padding: 0.5rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #e5e7eb;
}

/* Fila principal clickable */
.inventario-item-main {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  color: inherit;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.inventario-item-main:focus-visible {
  outline: 2px solid rgba(129, 140, 248, 0.9);
  border-radius: 0.6rem;
}

.inventario-info {
  min-width: 0;
}

.inventario-nombre {
  font-weight: 500;
}

.inventario-token {
  font-size: 0.7rem;
  color: #9ca3af;
}

.inventario-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: flex-end;
  margin-left: auto;
}

.inventario-expand-indicator {
  font-size: 0.7rem;
  color: #9ca3af;
}

/* Segunda línea (otros estados) */
.inventario-extra {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Bloque de unidades */
.inventario-unidades-block {
  margin-top: 0.3rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: radial-gradient(
    circle at 0 0,
    rgba(30, 64, 175, 0.25),
    rgba(15, 23, 42, 0.98)
  );
  padding: 0.45rem 0.5rem 0.5rem;
}

.unidades-header {
  margin-bottom: 0.35rem;
}

.unidades-title {
  font-size: 0.8rem;
  color: #e5e7eb;
}

.unidades-subtitle {
  font-size: 0.72rem;
  color: #9ca3af;
}

.unidades-loading,
.unidades-empty {
  min-height: 80px;
}

/* Lista de unidades */
.unidades-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 230px;
  overflow: auto;
}

.unidad-row {
  border-radius: 0.65rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.3rem 0.45rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.unidad-row--highlight {
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.7);
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.35),
    rgba(15, 23, 42, 0.98)
  );
}

.unidad-main {
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
  align-items: flex-start;
}

.unidad-serie-token {
  min-width: 0;
}

.unidad-serie {
  font-weight: 500;
}

.unidad-token {
  font-size: 0.7rem;
  color: #94a3b8;
}

.unidad-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  justify-content: flex-end;
}

/* Chips */
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.72rem;
  padding: 0.16rem 0.6rem;
  border: 1px solid transparent;
}

.chip-soft {
  background: rgba(30, 64, 175, 0.4);
  color: #e5e7eb;
  border-color: rgba(148, 163, 184, 0.6);
}

.chip-soft--total {
  background: rgba(55, 65, 81, 0.7);
  border-color: rgba(148, 163, 184, 0.9);
}

.chip-soft--almacen {
  background: rgba(22, 163, 74, 0.2);
  border-color: rgba(22, 163, 74, 0.9);
}

.chip-soft--asignado {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.9);
}

.chip-soft--resguardo {
  background: rgba(234, 179, 8, 0.18);
  border-color: rgba(234, 179, 8, 0.9);
}

.chip-soft--merma {
  background: rgba(248, 113, 113, 0.18);
  border-color: rgba(248, 113, 113, 0.9);
}

.chip-soft--devuelto {
  background: rgba(16, 185, 129, 0.18);
  border-color: rgba(16, 185, 129, 0.9);
}

.chip-soft--entrada {
  background: rgba(55, 65, 81, 0.6);
  border-color: rgba(148, 163, 184, 0.9);
}

.chip-soft--ubicacion {
  background: rgba(79, 70, 229, 0.3);
  border-color: rgba(129, 140, 248, 0.95);
}

.chip-outline {
  background: rgba(15, 23, 42, 0.96);
  border-color: rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
}

.chip-estado--almacen {
  border-color: rgba(34, 197, 94, 0.9);
}

.chip-estado--asignado {
  border-color: rgba(59, 130, 246, 0.9);
}

.chip-estado--resguardo {
  border-color: rgba(234, 179, 8, 0.9);
}

.chip-estado--merma {
  border-color: rgba(248, 113, 113, 0.9);
}

.chip-estado--devuelto {
  border-color: rgba(16, 185, 129, 0.9);
}

/* Estados vacíos / carga */
.estado-centrado {
  min-height: 140px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.8rem;
  text-align: center;
}

.estado-vacio {
  color: #9ca3af;
}

/* Spinner "spring" */
.spinner {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 3px solid rgba(115, 145, 255, 0.25);
  border-top-color: rgba(132, 165, 255, 0.95);
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transición expand/collapse */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.16s ease-out,
    transform 0.16s ease-out;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

/* Scrollbars finos para este componente */
.inventario-list,
.unidades-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.7) transparent;
}

.inventario-list::-webkit-scrollbar,
.unidades-list::-webkit-scrollbar {
  width: 6px;
}

.inventario-list::-webkit-scrollbar-track,
.unidades-list::-webkit-scrollbar-track {
  background: transparent;
}

.inventario-list::-webkit-scrollbar-thumb,
.unidades-list::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.7);
  border-radius: 999px;
}

/* Responsive */
@media (max-width: 900px) {
  .inventario-item-main {
    align-items: flex-start;
    flex-direction: column;
  }

  .inventario-counts {
    justify-content: flex-end;
    width: 100%;
  }

  .inventario-expand-indicator {
    align-self: flex-end;
  }

  .unidad-main {
    flex-direction: column;
  }

  .unidad-meta {
    justify-content: flex-end;
    align-self: flex-end;
    margin-left: 0;
  }
}
</style>
